import { useMemo } from 'react';
import Fuse, { type IFuseOptions } from 'fuse.js';
import { useCMSContent } from '@/hooks/useCMSContent';
import { SEO_PAGES } from './seo-config';

export type SearchType =
  | 'Services'
  | 'Industries'
  | 'Insights'
  | 'Resources'
  | 'Case Studies'
  | 'Careers'
  | 'Company';

export interface SearchDoc {
  id: string;
  title: string;
  description: string;
  headings: string;
  keywords: string;
  body: string;
  url: string;
  type: SearchType;
  icon: string;
}

/** Lowercase synonym → related terms, used to broaden fuzzy matching. */
export const SYNONYMS: Record<string, string[]> = {
  lawson: ['infor', 'cloudsuite', 'erp'],
  infor: ['lawson', 'cloudsuite', 'erp'],
  cloudsuite: ['infor', 'lawson'],
  m365: ['microsoft', 'office', 'copilot'],
  o365: ['microsoft', 'office', 'copilot'],
  'microsoft 365': ['microsoft', 'office', 'copilot'],
  office: ['microsoft', 'm365'],
  copilot: ['microsoft', 'ai', 'automation'],
  msp: ['managed', 'services'],
  managed: ['msp', 'services'],
  jobs: ['careers', 'hiring', 'positions', 'apply'],
  job: ['careers', 'hiring'],
  career: ['jobs', 'hiring'],
  hiring: ['careers', 'jobs'],
  apply: ['careers', 'jobs'],
  erp: ['infor', 'workday', 'oracle', 'microsoft'],
  workday: ['hcm', 'payroll', 'erp'],
  payroll: ['workday', 'infor', 'hcm'],
  ai: ['copilot', 'automation', 'machine', 'learning'],
  automation: ['ai', 'copilot', 'rpa'],
  helpdesk: ['service', 'desk', 'support'],
  support: ['helpdesk', 'service', 'desk', 'managed'],
  staffing: ['recruiting', 'talent', 'consultants'],
  consultants: ['staffing', 'talent'],
  government: ['municipal', 'public', 'sector'],
  municipal: ['government', 'public', 'sector'],
  cost: ['price', 'pricing', 'fee'],
  price: ['cost', 'pricing', 'fee'],
  cloud: ['aws', 'azure', 'google'],
  security: ['cybersecurity', 'compliance'],
  cybersecurity: ['security', 'compliance'],
};

function typeFor(path: string): SearchType {
  if (path.startsWith('/services')) return 'Services';
  if (path.startsWith('/industries')) return 'Industries';
  if (path.startsWith('/insights') || path.startsWith('/blog')) return 'Insights';
  if (path.startsWith('/resources')) return 'Resources';
  if (path.startsWith('/case-studies')) return 'Case Studies';
  if (path.startsWith('/careers')) return 'Careers';
  return 'Company';
}

const TYPE_ICON: Record<SearchType, string> = {
  Services: 'ri-tools-line',
  Industries: 'ri-building-4-line',
  Insights: 'ri-article-line',
  Resources: 'ri-book-open-line',
  'Case Studies': 'ri-bar-chart-box-line',
  Careers: 'ri-briefcase-line',
  Company: 'ri-information-line',
};

/** Static docs derived from the authoritative SEO route config. */
const STATIC_DOCS: SearchDoc[] = Object.entries(SEO_PAGES)
  .filter(([path]) => path !== '/privacy-policy' && path !== '/terms-of-service' && path !== '/search')
  .map(([path, meta]) => {
    const type = typeFor(path);
    return {
      id: `route-${path}`,
      title: meta.title.split(' | ')[0],
      description: meta.description,
      headings: '',
      keywords: meta.keywords,
      body: meta.description,
      url: path,
      type,
      icon: TYPE_ICON[type],
    };
  });

export const FUSE_OPTIONS: IFuseOptions<SearchDoc> = {
  includeScore: true,
  includeMatches: false,
  ignoreLocation: true,
  threshold: 0.4,
  minMatchCharLength: 2,
  keys: [
    { name: 'title', weight: 0.4 },
    { name: 'keywords', weight: 0.2 },
    { name: 'headings', weight: 0.15 },
    { name: 'description', weight: 0.15 },
    { name: 'body', weight: 0.1 },
  ],
};

/** Appends synonym expansions to the raw query for broader matching. */
export function expandQuery(q: string): string {
  const terms = q.toLowerCase().match(/[a-z0-9]+/g) || [];
  const extra = new Set<string>();
  terms.forEach((t) => (SYNONYMS[t] || []).forEach((s) => extra.add(s)));
  return [q, ...Array.from(extra)].join(' ').trim();
}

export interface SearchIndex {
  docs: SearchDoc[];
  search: (query: string, limit?: number) => SearchDoc[];
}

/** Builds the full fuzzy search index from static routes + live CMS content. */
export function useSearchIndex(): SearchIndex {
  const { articles, resources, careers, blogPosts } = useCMSContent();

  const docs = useMemo(() => {
    const list: SearchDoc[] = [...STATIC_DOCS];

    (blogPosts || []).forEach((b: Record<string, unknown>) => {
      if (b.status && b.status !== 'published') return;
      const tags = Array.isArray(b.tags) ? (b.tags as string[]).join(' ') : '';
      const body = String(b.body || '').replace(/[#*_>`]/g, ' ').slice(0, 600);
      list.push({
        id: `blog-${b.id}`,
        title: String(b.title || ''),
        description: String(b.excerpt || ''),
        headings: String(b.category || ''),
        keywords: tags,
        body,
        url: `/blog/${b.slug || ''}`,
        type: 'Insights',
        icon: TYPE_ICON.Insights,
      });
    });

    (articles || []).forEach((a: Record<string, unknown>) => {
      // Drafts/scheduled articles must never surface in public search.
      if (a.status && a.status !== 'published') return;
      const body = String(a.body || '').replace(/[#*_>`]/g, ' ').slice(0, 600);
      const tags = Array.isArray(a.tags) ? (a.tags as string[]).join(' ') : '';
      list.push({
        id: `article-${a.id}`,
        title: String(a.title || ''),
        description: String(a.excerpt || ''),
        headings: String(a.category || ''),
        keywords: tags,
        body,
        url: `/insights/${a.slug || ''}`,
        type: 'Insights',
        icon: TYPE_ICON.Insights,
      });
    });

    (resources || []).forEach((r: Record<string, unknown>) => {
      const isCase = String(r.type || '') === 'Case Study';
      const tags = Array.isArray(r.tags) ? (r.tags as string[]).join(' ') : '';
      list.push({
        id: `resource-${r.id}`,
        title: String(r.title || ''),
        description: String(r.description || ''),
        headings: String(r.topic || ''),
        keywords: `${tags} ${r.type || ''}`,
        body: `${r.excerpt || ''} ${r.topic || ''}`.slice(0, 600),
        url: isCase ? '/case-studies' : `/resources/${r.slug || ''}`,
        type: isCase ? 'Case Studies' : 'Resources',
        icon: isCase ? TYPE_ICON['Case Studies'] : TYPE_ICON.Resources,
      });
    });

    (careers || []).forEach((c: Record<string, unknown>) => {
      if (c.active === false) return;
      list.push({
        id: `career-${c.id}`,
        title: String(c.title || ''),
        description: `${c.department || ''} · ${c.location || ''} · ${c.type || ''}`.replace(/^ · /, ''),
        headings: String(c.department || ''),
        keywords: `${c.department || ''} ${c.location || ''} ${c.type || ''} careers jobs`,
        body: String(c.description || '').slice(0, 600),
        url: `/careers/${c.slug || c.id || ''}`,
        type: 'Careers',
        icon: TYPE_ICON.Careers,
      });
    });

    return list;
  }, [articles, resources, careers, blogPosts]);

  const fuse = useMemo(() => new Fuse(docs, FUSE_OPTIONS), [docs]);

  const search = useMemo(
    () => (query: string, limit = 24) => {
      const q = query.trim();
      if (!q) return [];
      return fuse.search(expandQuery(q), { limit }).map((r) => r.item);
    },
    [fuse],
  );

  return { docs, search };
}

/** Word-boundary-safe highlight used by the command palette + results page. */
export function highlightRanges(text: string, query: string): Array<{ text: string; match: boolean }> {
  const q = query.trim();
  if (!q) return [{ text, match: false }];
  const terms = Array.from(
    new Set(
      (q.toLowerCase().match(/[a-z0-9]+/g) || [])
        .filter((t) => t.length > 1)
        .flatMap((t) => [t, ...(SYNONYMS[t] || [])])
        .filter((t) => t.length > 1),
    ),
  );
  if (terms.length === 0) return [{ text, match: false }];

  const escaped = terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts: Array<{ text: string; match: boolean }> = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push({ text: text.slice(last, m.index), match: false });
    parts.push({ text: m[0], match: true });
    last = m.index + m[0].length;
    if (m[0].length === 0) re.lastIndex++;
  }
  if (last < text.length) parts.push({ text: text.slice(last), match: false });
  return parts.length ? parts : [{ text, match: false }];
}

export const POPULAR_LINKS: SearchDoc[] = [
  { id: 'p-managed', title: 'Managed IT Services', description: 'Dallas–Fort Worth managed & co-managed IT', headings: '', keywords: 'msp managed', body: '', url: '/services/managed', type: 'Services', icon: TYPE_ICON.Services },
  { id: 'p-infor', title: 'Infor Lawson & CloudSuite Support', description: 'Managed Infor ERP support', headings: '', keywords: 'infor lawson', body: '', url: '/services/erp/infor-support', type: 'Services', icon: TYPE_ICON.Services },
  { id: 'p-workday', title: 'Workday Support & AMS', description: 'Post-go-live Workday support', headings: '', keywords: 'workday', body: '', url: '/services/erp/workday-support', type: 'Services', icon: TYPE_ICON.Services },
  { id: 'p-staffing', title: 'Staffing & Consultants', description: 'Infor, Workday, Oracle & Microsoft consultants', headings: '', keywords: 'staffing consultants', body: '', url: '/services/staffing', type: 'Services', icon: TYPE_ICON.Services },
  { id: 'p-careers', title: 'Careers', description: 'Open positions at Cendien', headings: '', keywords: 'jobs careers', body: '', url: '/careers', type: 'Careers', icon: TYPE_ICON.Careers },
  { id: 'p-contact', title: 'Contact Us', description: 'Get in touch with our team', headings: '', keywords: 'contact', body: '', url: '/contact', type: 'Company', icon: TYPE_ICON.Company },
];